import { ApiSchema } from "@api-types";
import { captureException } from "@sentry/react";
import { ErrorView } from "components/ErrorView/ErrorView";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { hasPolicy$ } from "helpers/hasPolicy";
import React, { useEffect, useState } from "react";
import { useSubscription } from "rsm/useSubscription";
import { of } from "rxjs";
import { getFFService } from "services/ff.service";
import { churnBlocked$ } from "hooks/useIsChurned";
import { ChurnBlockedView } from "components/ChurnBlockedView/ChurnBlockedView";

const PageRoute: React.FC<{
  El: React.LazyExoticComponent<React.FC<{}>>;
  inPolicy?: ApiSchema.Policy;
  ff?: ApiSchema.FF;
  /** When true, this page is blocked for restricted (churned) non-admin customers. */
  blockWhenChurned?: boolean;
}> = ({ El, inPolicy, ff, blockWhenChurned }) => {
  const [isAllowed, setIsAllowed] = useState<boolean | null>(() => (inPolicy ? null : true));
  const [isChurnBlocked, setIsChurnBlocked] = useState<boolean | null>(() =>
    blockWhenChurned ? null : false,
  );
  useSubscription(
    inPolicy ? hasPolicy$(inPolicy) : of(true),
    (hasPolicy) => {
      setIsAllowed(inPolicy && !hasPolicy ? false : true);
    },
    [inPolicy],
  );
  useSubscription(blockWhenChurned ? churnBlocked$ : of(false), (blocked) => setIsChurnBlocked(blocked), [
    blockWhenChurned,
  ]);
  useSubscription(
    ff ? getFFService().featureEnabled$(ff) : of(true),
    (hasFeature) => {
      if (!hasFeature) {
        window.location.href = "/";
      }
    },
    [ff],
  );

  useEffect(() => {
    if (isAllowed !== false) return;
    captureException(new Error("PageRoute is not allowed"), {
      tags: {
        inPolicy: inPolicy ?? "n/a",
        path: window.location.pathname,
      },
    });
  }, [isAllowed, inPolicy]);

  if (isChurnBlocked === true) {
    return <ChurnBlockedView />;
  }

  if (isAllowed === true && isChurnBlocked === false) {
    return (
      <React.Suspense fallback={<LoadingPage />}>
        <El />
      </React.Suspense>
    );
  } else if (isAllowed === false) {
    return (
      <ErrorView
        errorCode={403}
        title="Access denied"
        summary="You are not authorized to access this page."
        showHomeButton={true}
      />
    );
  }

  return <LoadingPage />;
};

export default PageRoute;
